import { Controller } from "@hotwired/stimulus"

// Auto-dismisses a flash toast after a few seconds, or immediately via its
// close button — both paths go through the same fade-then-remove so neither
// looks abrupt, and closing early cancels the pending auto-dismiss.
//
// The countdown pauses while the toast is hovered or holds keyboard focus,
// and restarts in full when it's left (WCAG 2.2.1 Timing Adjustable).
// A dismissAfter of 0 turns auto-dismiss off entirely.
export default class extends Controller {
  static values = { dismissAfter: { type: Number, default: 6000 } }

  connect() {
    this.pause = this.pause.bind(this)
    this.resume = this.resume.bind(this)
    for (const event of ["mouseenter", "focusin"]) this.element.addEventListener(event, this.pause)
    for (const event of ["mouseleave", "focusout"]) this.element.addEventListener(event, this.resume)
    this.resume()
  }

  disconnect() {
    clearTimeout(this.timeout)
    for (const event of ["mouseenter", "focusin"]) this.element.removeEventListener(event, this.pause)
    for (const event of ["mouseleave", "focusout"]) this.element.removeEventListener(event, this.resume)
  }

  pause() {
    clearTimeout(this.timeout)
  }

  resume(event) {
    if (this.dismissAfterValue <= 0) return
    // focusout fires when focus moves between children; only resume once it
    // has actually left the toast.
    if (event?.type === "focusout" && this.element.contains(event.relatedTarget)) return
    if (event?.type === "mouseleave" && this.element.contains(document.activeElement)) return

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.close(), this.dismissAfterValue)
  }

  close() {
    clearTimeout(this.timeout)
    this.element.addEventListener("transitionend", () => this.element.remove(), { once: true })
    this.element.classList.add("opacity-0", "-translate-y-1")
  }
}
